import { ComponentPropsWithoutRef, ComponentType, FC } from 'react';

import classNames from 'classnames';

import EmailCircle from '@uikit/icons/EmailCircle';
import FacebookCircle from '@uikit/icons/FacebookCircle';
import LinkedInCircle from '@uikit/icons/LinkedInCircle';
import NewTwitterLogo from '@uikit/icons/NewTwitterLogo';
import RedditCircle from '@uikit/icons/RedditCircle';
import TelegramCircle from '@uikit/icons/TelegramCircle';
import WhatsappCircle from '@uikit/icons/WhatsappCircle';

import {
  PlatformConfiguration,
  PlatformDetails,
  socialLinkGenerators,
  SocialPlatformType,
} from '@store:web/components/common/ShareSocialsList/utils';

const defaultPlatformIcons: Record<SocialPlatformType, ComponentType> = {
  Facebook: FacebookCircle,
  Twitter: NewTwitterLogo,
  LinkedIn: LinkedInCircle,
  Telegram: TelegramCircle,
  WhatsApp: WhatsappCircle,
  Reddit: RedditCircle,
  Email: EmailCircle,
};

const generatePlatformShareLink = (platform: SocialPlatformType, config: PlatformDetails) => {
  const linkGenerator = socialLinkGenerators[platform];

  return linkGenerator ? linkGenerator(config) : '';
};

export interface SocialShareListProps extends ComponentPropsWithoutRef<'ul'> {
  platformConfigurations: Partial<PlatformConfiguration>;
}

const SocialShareList: FC<SocialShareListProps> = ({ className, platformConfigurations }) => {
  const platforms = Object.keys(platformConfigurations) as SocialPlatformType[];

  if (platforms.length === 0) {
    return null;
  }

  return (
    <ul className={classNames('flex flex-wrap gap-3', className)}>
      {platforms.map((platform) => {
        const config = platformConfigurations[platform];

        if (!config) {
          return null;
        }

        const Icon = config?.Icon ?? defaultPlatformIcons[platform];
        const shareLink = generatePlatformShareLink(platform, config);

        return (
          <li key={platform}>
            <a
              href={shareLink}
              className="icon-[1.5rem] sm:icon-xl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon />
            </a>
          </li>
        );
      })}
    </ul>
  );
};

export default SocialShareList;
